import React from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import './Modal.css';

/**
 * Reusable Modal component for dialogs
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {string} title - Modal title
 * @param {React.ReactNode} children - Modal content
 * @param {array} actions - Array of {label, onClick, className, isLoading, loadingLabel} objects (for footer buttons)
 * @param {boolean} closeOnOverlay - Click on overlay to close
 * @param {boolean} isLoading - Global loading state (disables all actions)
 * @param {boolean} disableCloseWhenLoading - Prevent closing during loading
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  actions = [],
  closeOnOverlay = true,
  isLoading = false,
  disableCloseWhenLoading = true,
}) => {
  if (!isOpen) return null;

  const shouldDisableOverlay = disableCloseWhenLoading && isLoading;

  return (
    <div className="modal-overlay" onClick={!shouldDisableOverlay && closeOnOverlay ? onClose : undefined}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="modal-header">
            <h2 className="modal-title">{title}</h2>
            <button 
              className="modal-close" 
              onClick={onClose} 
              aria-label="Close"
              disabled={shouldDisableOverlay}
            >
              ×
            </button>
          </div>
        )}

        <div className="modal-body">{children}</div>

        {actions.length > 0 && (
          <div className="modal-footer">
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={action.onClick}
                className={`modal-btn ${action.className || ''}`}
                disabled={isLoading || action.isLoading}
              >
                {isLoading || action.isLoading ? (
                  <span className="button-loading">
                    <CircleNotch size={14} weight="fill" className="spinner" />
                    {action.loadingLabel || action.label}
                  </span>
                ) : (
                  action.label
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
